import { useBanner, type BannerState } from "@/hooks/use-banner";
import CloseIcon from "@/icons/CloseIcon";
import BlackFridayIcon from "@/icons/BlackFridayIcon";
import ThirtyOffIcon from "@/icons/ThirtyOffIcon";
import ExploreNowIcon from "@/icons/ExploreNowIcon";

const START_DATE = new Date("2025-11-25T00:00:00Z");
const END_DATE = new Date("2025-12-02T00:00:00Z"); // Dec 1 inclusive, so end is Dec 2

export const BlackFridayBanner = () => {
  const { isVisible, updateState } = useBanner({
    slug: 'black-friday-banner',
    defaultState: {
      dismissed: false,
      dismissedAt: null,
    },
    visibilityCheck: (state: BannerState) => {
      // Don't show if dismissed
      if (state.dismissed) {
        return false;
      }

      // Check if current date is within the promotion period
      const now = new Date();
      if (now >= START_DATE && now < END_DATE) {
        return true;
      }

      return false;
    },
  });

  const handleDismiss = async () => {
    await updateState({
      dismissed: true,
      dismissedAt: Date.now(),
    });
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="cy:relative cy:w-full cy:bg-black cy:h-[62px] cy:overflow-hidden">
      <div className="cy:max-w-full cy:w-full cy:h-full cy:mx-auto">
        <div className="cy:flex cy:items-center cy:justify-between cy:gap-4 cy:relative cy:h-[62px] cy:px-6 cy:py-0">
          {/* Close button - top right with proper spacing */}
          <button
            onClick={handleDismiss}
            className="cy:absolute cy:top-[19px] cy:right-[18px] cy:text-white cy:hover:text-gray-300 cy:transition-colors cy:flex cy:items-center cy:justify-center cy:p-1 cy:z-10 cy:cursor-pointer cy:bg-transparent cy:border-0"
            aria-label="Dismiss banner"
          >
            <CloseIcon className="cy:w-[7px] cy:h-[7px]" />
          </button>

          {/* Left section: BLACK FRIDAY with OFF badge */}
          <div className="cy:flex cy:items-center cy:relative cy:flex-shrink-0">
            <BlackFridayIcon className="cy:h-[62px] cy:w-[263px] cy:block" />
          </div>

          {/* Center section: 30% OFF badge */}
          <div className="cy:flex-shrink-0 cy:flex cy:items-center">
            <ThirtyOffIcon className="cy:w-[59px] cy:h-[62px]" />
          </div>

          {/* Center-right section: Descriptive text */}
          <div className="cy:flex-1 cy:flex cy:items-center cy:min-w-0 cy:pr-4">
            <p className="cy:text-white cy:text-[14px] cy:leading-[156%] cy:font-normal cy:m-0">
              Limited time offer to get comprehensive accessibility audits with{" "}
              <span className="cy:font-bold">WebYes Accessibility</span>.
            </p>
          </div>

          {/* Right section: Explore now button with spacing from close button */}
          <div className="cy:flex-shrink-0 cy:flex cy:items-center cy:mr-[18px]">
            <a
              href="https://accessibility.webyes.com/signUp?utm_source=Pluginscreen&utm_medium=AccessibilityPlus&utm_campaign=BlackFriday2025&utm_content=Banner"
              target="_blank"
              rel="noopener noreferrer"
              className="cy:inline-flex cy:items-center cy:justify-center cy:bg-[#155DFC] cy:hover:bg-[#1249CC] cy:text-white cy:px-5 cy:py-2.5 cy:rounded-full cy:transition-colors cy:cursor-pointer cy:no-underline"
            >
              <ExploreNowIcon className="cy:w-[82px] cy:h-[14px]" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

